import * as $ from 'jquery';
import { disposable, disposableSubscription, serialDisposable } from 'common/barrels/core';
import { ValidationSummary, ValidationItem } from 'shared/data/validation';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { registerBinding } from 'runtime/bindingengine';
import * as rxui from 'common/rxui/core';
import { ValidationSummaryRequested } from 'runtime/events/validationsummaryrequested';
import { Color } from 'common/color';

import 'runtime/styles/controls/validationcontrol.less';

export class ValidationControl extends Control {
    validationSummary: ValidationSummary;

    private listDisposable = serialDisposable(null);

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);
        this.$element
            .addClass('control-container validationcontrol noselect')
            .conditionalCss(this.config['content.Foreground'], 'color', () => Color.splitArgb(this.config['content.Foreground'])[0])
            .applyBackground(this.config['content.Background']);

        this.subscriptions = disposableSubscription(
            this.processBindings(this.config),
            rxui.click(this.$element).map(x => $(x.target)).filter($x => $x.hasClass('validationcontrol-toggle') || $x.hasClass('validationcontrol-result')).subscribe($i => this.toggleExpansion($i)),
            rxui.mousedownleft(this.$element).filter(x => $(x.target).hasClass('validationcontrol-toggle')).subscribe(x => x.preventDefault()),
            rxui.mousedownleft(this.$element).map(x => ({ event: x, $target: $(x.target) })).filter(x => x.$target.is('li')).subscribe(x => {
                x.event.preventDefault();
                x.$target.find('.validationcontrol-focuscontainer').first().focus();
            }),
            rxui.focusin(this.$element).switchMap(() => rxui.keyEvent(this.$element).takeUntil(rxui.focusout(this.$element))).subscribe(x => this.handleKey(x))
            // silenced for now; double-click selects text, and text selection is back in, so this will look bad and / or drive bad behaviour
            //rxui.dblclick(this.$element).select(x => $(x.target)).where(x => x.hasClass('validationcontrol-resulttext')).subscribe(x => this.toggleExpansion(x.closest('li').find('i').first()))
        );

        this.applyTooltip(this.$element);

        this.initialize();
    }

    private handleKey(e: KeyboardEvent) {
        var $f = this.$element.find(':focus');
        var $nodes = this.$element.find('.validationcontrol-focuscontainer:visible');
        var index = $nodes.index($f);
        var selectNext = () => $nodes.eq(Math.min($nodes.length, index + 1)).focus();
            
        switch(e.keyCode) {
            case $.ui.keyCode.DOWN:
                selectNext();
                break;

            case $.ui.keyCode.UP:
                $nodes.eq(Math.max(0, index - 1)).focus();
                break;

            case $.ui.keyCode.LEFT:
                if ($f.closest('li').hasClass('validationcontrol-collapsed') || !this.hasChildren($f))
                    $f.closest('li').parent().closest('li').find('.validationcontrol-focuscontainer').first().focus();
                else
                    this.toggleExpansion($f.parent().find('i').eq(0));

                break;

            case $.ui.keyCode.RIGHT:
                if ($f.closest('li').hasClass('validationcontrol-collapsed'))
                    this.toggleExpansion($f.parent().find('i').eq(0));
                else if (this.hasChildren($f))
                    selectNext();

                break;
        }
    }

    private toggleExpansion($i: JQuery) {
        if (!this.hasChildren($i))
            return;

        var $li = $i.closest('li');
        var collapsed = $li.hasClass('validationcontrol-collapsed');
        $li.toggleClass('validationcontrol-collapsed');
        $li.find('.validationcontrol-toggle').first().toggleClass('fa-caret-right', !collapsed).toggleClass('fa-caret-down', collapsed);
    }

    private hasChildren($n) {
        return $n.closest('li').children().last().is('ul');
    }

    'update Summary'(summary) {
        var event = new ValidationSummaryRequested();
        this.eventAggregator.publish(event);
        this.validationSummary = event.validationSummary;

        this.render();
    }

    'clear Summary'() {
        this.validationSummary = null;

        this.render();
    }

    collapseAll() {
        const $elements = this.$element.find('.validationcontrol-toggle');
        $elements.each((i, x) => {
            const $x = $(x);
            $x.closest('li').addClass('validationcontrol-collapsed');
            $x.addClass('fa-caret-right').removeClass('fa-caret-down');
        });
    }

    expandAll() {
        const $elements = this.$element.find('.validationcontrol-toggle');
        $elements.each((i, x) => {
            const $x = $(x);
            $x.closest('li').removeClass('validationcontrol-collapsed');
            $x.removeClass('fa-caret-right').addClass('fa-caret-down');
        });
    }

    expandErrors() {
        this.collapseAll();
        const $elements = this.$element.find('.validationcontrol-itemcontainer');
        const $filtered = $elements.has('.validationcontrol-fail');
        
        $filtered.each((i, x) => {
            const $x = $(x);
            $x.closest('li').removeClass('validationcontrol-collapsed');
            $x.children('.validationcontrol-toggle').first().removeClass('fa-caret-right').addClass('fa-caret-down');
        });
    }

    render() {
        if (this.validationSummary) {
            const $list = this.validationSummary.validationItems.reduce(($a, x) => $a.append(this.buildTree(x)), $('<ul />'));
            this.$element.append($list);
            this.listDisposable.set(disposable(() => $list.remove()));
        } else {
            this.listDisposable.set(null);    
        }
    }

    private buildTree(item: ValidationItem) {
        return this.buildElement(item)
            .append(this.hasItems(item)
                ? (<ValidationSummary>item).validationItems.reduce(($a, x) => $a.append(this.buildTree(x)), $('<ul />'))
                : $());
    }

    private buildElement(item: ValidationItem): JQuery {
        return $('<li />').append($('<div />')
            .addClass('validationcontrol-itemcontainer')
            .append(this.makeExpansionIcon(item))
            .append($('<div />').addClass('validationcontrol-focuscontainer').prop('tabindex', 0)
                .append(this.makeResultIcon(item))
                .append($('<span />').addClass('validationcontrol-resulttext').text(item.message))
            ))
            .toggleClass('validationcontrol-collapsed', item.validationState === ValidationItem.pass);;
    }

    private makeResultIcon(item: ValidationItem) : JQuery {
        switch (item.validationState.toLowerCase()) {
            case ValidationItem.pass:
                return $('<i />').addClass('fa fa-check-circle validationcontrol-result validationcontrol-pass');

            case ValidationItem.warn:
                return $('<i />').addClass('fa fa-exclamation-triangle validationcontrol-result validationcontrol-warn');

            case ValidationItem.fail:
                return $('<i />').addClass('fa fa-times-circle validationcontrol-result validationcontrol-fail');

            default:
                throw new Error('Unknown validation state: ' + item.validationState);
        }
    }

    private makeExpansionIcon(item: ValidationItem) {
        return $('<i />').addClass(
            !this.hasItems(item) ? 'validationcontrol-noexpansion'
            : item.validationState === ValidationItem.pass ? 'fa fa-caret-right validationcontrol-expander validationcontrol-toggle'
            : 'fa fa-caret-down validationcontrol-expander validationcontrol-toggle'
        );
    }

    private hasItems(item: ValidationItem) {
        return (item instanceof ValidationSummary) && (item.validationItems !== null) && (item.validationItems.length > 0);
    }

    focus() {
        this.$element.find('.validationcontrol-focuscontainer:first').focus()
    }

    tabIndexElements() {
        return $();
    }

    protected applyScrollVetoBehavior() { }
}

registerBinding('validationcontrol', ValidationControl);